import { defineStore } from 'pinia'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'

export const useNotificationsStore = defineStore('NotificationsStore', {
  state: () => ({
    notifications: [],
    snackbarNotifications: [],
    oldNotifications: [],
    preferSnackbar: true,
    notAuthorizedPageSubject: null,
  }),
  getters: {
    // Getter for old notifications count
    oldNotificationsCount: state => state.oldNotifications.length,

    // Getter for recent old notifications (last 10)
    recentOldNotifications: state => state.oldNotifications.slice(-10).reverse(),

    // Getter for old notifications grouped by type
    oldNotificationsByType: state => {
      return state.oldNotifications.reduce((acc, notification) => {
        if (!acc[notification.type]) {
          acc[notification.type] = []
        }
        acc[notification.type].push(notification)

        return acc
      }, {})
    },
  },
  actions: {
    showNotifications() {
      const toast = useToast()

      this.notifications.forEach(notification => {
        toast[notification.type](notification.message, notification.context)
      })
      this.clearNotifications()
    },

    addSnackbarNotification(notification) {
      const timestamp = new Date().toISOString()

      // Add page subject to message if it exists to keep error message & context separate
      const pageSubject = notification.context?.pageSubject || null
      if (pageSubject) {
        notification.message = notification.message + ` ${pageSubject}`
      }

      const newNotification = {
        ...notification,
        id: Date.now() + Math.random(),
        show: true,
        timestamp,

        // Preserve previewPage context for redirect functionality
        previewPage: notification.context?.previewPage || null,
      }

      console.debug('NotificationsStore: Adding snackbar notification:', newNotification)
      this.snackbarNotifications.push(newNotification)
      console.debug(
        'NotificationsStore: Total snackbar notifications:',
        this.snackbarNotifications.length
      )
    },

    removeSnackbarNotification(id) {
      const index = this.snackbarNotifications.findIndex(n => n.id === id)
      if (index > -1) {
        const notification = this.snackbarNotifications[index]

        // Store the notification in oldNotifications with additional metadata
        const oldNotification = {
          ...notification,
          dismissedAt: new Date().toISOString(),
          dismissedBy: 'user', // Could be 'user', 'timeout', 'system', etc.
        }

        this.oldNotifications.push(oldNotification)
        console.debug(
          'NotificationsStore: Moved notification to oldNotifications:',
          oldNotification
        )

        // Remove from active notifications
        this.snackbarNotifications.splice(index, 1)
      }
    },

    clearSnackbarNotifications() {
      // Move all current notifications to oldNotifications before clearing
      const dismissedAt = new Date().toISOString()

      this.snackbarNotifications.forEach(notification => {
        const oldNotification = {
          ...notification,
          dismissedAt,
          dismissedBy: 'system', // Cleared by system action
        }

        this.oldNotifications.push(oldNotification)
      })

      console.debug(
        'NotificationsStore: Moved',
        this.snackbarNotifications.length,
        'notifications to oldNotifications'
      )
      this.snackbarNotifications = []
    },

    setNotificationPreference(preferSnackbar = false) {
      this.preferSnackbar = preferSnackbar
      console.debug(
        `NotificationsStore: Preference set to ${preferSnackbar ? 'snackbar' : 'toast'}`
      )
    },

    // debug helper method
    testSnackbar() {
      console.debug('NotificationsStore: testSnackbar called, preferSnackbar:', this.preferSnackbar)
      this.setNotificationPreference(true) // Ensure preference is set
      this.addSnackbarNotification({
        type: 'success',
        message: 'Test snackbar notification - this should appear!',
        timeout: 5000,
      })
      console.debug(
        'NotificationsStore: Test snackbar notification added, array length:',
        this.snackbarNotifications.length
      )
    },

    // Global method to initialize snackbar mode
    enableSnackbarMode() {
      if (!this.preferSnackbar) {
        this.setNotificationPreference(true)
        console.debug('NotificationsStore: Snackbar mode enabled globally')
      }
    },
    clearNotifications() {
      this.notifications = []
    },

    notify(Notification) {
      const { level, title, message } = Notification
      if (level === 'info') {
        this.info(message, { title })
      } else if (level === 'error') {
        this.error(message, { title })
      } else if (level === 'warning') {
        this.warn(message, { title })
      } else if (level === 'success') {
        this.success(message, { title })
      }
    },
    error(message, context = {}) {
      // optionally accept an object with an error property
      // ex: notify.error({ error: Error.Auth.PAGE_FORBIDDEN, pageSubject: toSubject })
      if (message.error) {
        context = message
        message = context.error
      }
      if (this.preferSnackbar) {
        this.addSnackbarNotification({
          type: 'error',
          message,
          context,
          timeout: 6000,
        })
      } else {
        this.notifications.push({
          type: 'error',
          message,
          context,
        })
        this.showNotifications()
      }
    },
    info(message, context = {}) {
      if (this.preferSnackbar) {
        this.addSnackbarNotification({
          type: 'info',
          message,
          context,
          timeout: 4000,
        })
      } else {
        this.notifications.push({
          type: 'info',
          message,
          context,
        })
        this.showNotifications()
      }
    },
    warn(message, context = {}) {
      if (this.preferSnackbar) {
        this.addSnackbarNotification({
          type: 'warning',
          message,
          context,
          timeout: 5000,
        })
      } else {
        this.notifications.push({
          type: 'warning',
          message,
          context,
        })
        this.showNotifications()
      }
    },
    success(message, context = {}) {
      console.debug(
        'NotificationsStore: Success called with preferSnackbar:',
        this.preferSnackbar,
        'Message:',
        message
      )
      if (this.preferSnackbar) {
        this.addSnackbarNotification({
          type: 'success',
          message,
          context,
          timeout: 4000,
        })
      } else {
        this.notifications.push({
          type: 'success',
          message,
          context,
        })
        this.showNotifications()
      }
    },

    handleRedirectButton(context) {
      if (context.redirect) {
      }
    },

    // Handle navigation to preview page
    navigateToPreviewPage(previewPageParams) {
      if (previewPageParams && previewPageParams.name) {
        const router = useRouter()

        console.debug('NotificationsStore: Navigating to preview page:', previewPageParams)
        router.push(previewPageParams)
      }
    },

    // Utility methods for managing old notifications
    clearOldNotifications() {
      console.debug(
        'NotificationsStore: Clearing',
        this.oldNotifications.length,
        'old notifications'
      )
      this.oldNotifications = []
    },

    getOldNotificationsByType(type) {
      return this.oldNotifications.filter(notification => notification.type === type)
    },

    getOldNotificationsByDateRange(startDate, endDate) {
      return this.oldNotifications.filter(notification => {
        const notificationDate = new Date(notification.timestamp)

        return notificationDate >= startDate && notificationDate <= endDate
      })
    },

    getOldNotificationsByDismissalReason(dismissedBy) {
      return this.oldNotifications.filter(notification => notification.dismissedBy === dismissedBy)
    },

    // Get old notifications with pagination
    getOldNotificationsPaginated(page = 1, limit = 10) {
      const startIndex = (page - 1) * limit
      const endIndex = startIndex + limit

      return {
        notifications: this.oldNotifications.slice(startIndex, endIndex),
        total: this.oldNotifications.length,
        page,
        limit,
        totalPages: Math.ceil(this.oldNotifications.length / limit),
      }
    },

    // Remove old notifications older than specified days
    cleanupOldNotifications(olderThanDays = 30) {
      const cutoffDate = new Date()

      cutoffDate.setDate(cutoffDate.getDate() - olderThanDays)

      const initialCount = this.oldNotifications.length

      this.oldNotifications = this.oldNotifications.filter(notification => {
        const notificationDate = new Date(notification.timestamp)

        return notificationDate > cutoffDate
      })

      const removedCount = initialCount - this.oldNotifications.length

      console.debug(
        `NotificationsStore: Cleaned up ${removedCount} old notifications older than ${olderThanDays} days`
      )

      return removedCount
    },
  },
})
